<script setup lang="ts">
import { computed } from 'vue';
import { CircleCheck, TriangleAlert } from '@lucide/vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import type { AIValidationStatus } from '@/types/v2/Document';

const props = defineProps<{
  status?: AIValidationStatus | null;
  binderResultId?: number | null;
  binderCount: number;
  policyId: number;
  // Set where the cell sits inside a client's own policy list, so the review opens in
  // that client's context. Without it the link falls back to the account-agnostic
  // AI results page, which is the only option on surfaces with no account.
  accountId?: number | string | null;
}>();

const emit = defineEmits<{
  (e: 'open-modal', policyId: number): void;
}>();

const STATUS_DISPLAY = {
  validated: {
    label: 'Validated',
    tooltip: 'Verified binder scanning results',
    icon: CircleCheck,
    colorClass: 'text-green-700',
  },
  unvalidated: {
    label: 'Unvalidated',
    tooltip: 'Binder scanning results ready for review',
    icon: TriangleAlert,
    colorClass: 'text-amber-700',
  },
} as const;

const display = computed(() =>
  props.status === 'validated' || props.status === 'unvalidated' ? STATUS_DISPLAY[props.status] : null,
);

const showSingleLink = computed(
  () => display.value && props.binderCount === 1 && props.binderResultId != null,
);

const reviewRoute = computed(() =>
  props.accountId
    ? {
        name: 'account-binder-review',
        params: { id: String(props.accountId), promptResultId: String(props.binderResultId) },
      }
    : { path: '/ai-results', query: { resultId: props.binderResultId } },
);
const showMultiButton = computed(() => display.value && props.binderCount > 1);

function onClick() {
  emit('open-modal', props.policyId);
}
</script>

<template>
  <Tooltip
    v-if="display && (showSingleLink || showMultiButton)"
    :text="display.tooltip"
    position="top">
    <router-link
      v-if="showSingleLink"
      :to="reviewRoute"
      :aria-label="display.label"
      :title="display.label"
      :class="[display.colorClass, 'inline-flex', 'items-center']">
      <component
        :is="display.icon"
        :size="16" />
    </router-link>
    <button
      v-else
      type="button"
      :aria-label="display.label"
      :title="display.label"
      :class="[display.colorClass, 'inline-flex', 'items-center', 'cursor-pointer', 'bg-transparent', 'p-0', 'border-0']"
      @click="onClick">
      <component
        :is="display.icon"
        :size="16" />
    </button>
  </Tooltip>
  <!--
    Placeholder so an unscanned policy still occupies the column, keeping the icons in
    the rows above and below visually aligned.
  -->
  <span
    v-else
    class="text-gray-400"
    aria-hidden="true"
    >—</span
  >
</template>
